// Croqui: `components/DateRangeFilter.tsx`. Substitui `components/app/date-range-filter.tsx`.
//
// Presets Hoje · 7 dias · 30 dias · Personalizado (um `Segmented`) + botão com o intervalo
// vigente, que abre o popover de dois meses. As datas viajam como `YYYY-MM-DD` no fuso de
// São Paulo (`spec/13` §4.1), o `Date` do croqui é da maquete.
//
// Em 390px o controle vira um `Select` compacto (`spec/24` §4 item 4): quatro presets +
// um botão de intervalo não cabem em 350px.

import { useState } from 'react';
import { addDaysToIsoDate, formatDate, formatShortDate, todayIsoDate } from '../../lib/dates.ts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { Btn } from './btn.tsx';
import { CalendarMonth } from './calendar-month.tsx';
import { Divider } from './divider.tsx';
import { Icon } from './icon.tsx';
import { type SegItem, Segmented } from './segmented.tsx';
import { Select } from './select.tsx';

export type DatePreset = 'today' | '7d' | '30d' | 'custom';

export interface DateRangeValue {
  preset: DatePreset;
  /** `YYYY-MM-DD` em `America/Sao_Paulo`. */
  from: string;
  to: string;
}

const LABELS: Record<DatePreset, string> = {
  today: 'Hoje',
  '7d': '7 dias',
  '30d': '30 dias',
  custom: 'Personalizado',
};

const PRESETS: SegItem[] = [
  { id: 'today', label: LABELS.today },
  { id: '7d', label: LABELS['7d'] },
  { id: '30d', label: LABELS['30d'] },
];

export function rangeForPreset(preset: DatePreset, today = todayIsoDate()): DateRangeValue {
  const days = preset === 'today' ? 0 : preset === '7d' ? 6 : 29;
  return { preset, from: addDaysToIsoDate(today, -days), to: today };
}

function label(value: DateRangeValue): string {
  return value.from === value.to
    ? formatDate(`${value.from}T12:00:00Z`)
    : `${formatShortDate(`${value.from}T12:00:00Z`)}, ${formatDate(`${value.to}T12:00:00Z`)}`;
}

export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: value.from, to: value.to });

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {/* 390px: um `Select` no lugar do segmentado (`spec/24` §4 item 4). */}
      <Select
        small
        aria-label="Período"
        className="w-full sm:hidden"
        value={value.preset === 'custom' ? 'custom' : value.preset}
        onChange={(event) => {
          const next = event.target.value;
          if (next === 'custom') {
            setOpen(true);
            return;
          }
          if (next === 'today' || next === '7d' || next === '30d') {
            onChange(rangeForPreset(next));
          }
        }}
      >
        {(['today', '7d', '30d', 'custom'] as const).map((preset) => (
          <option key={preset} value={preset}>
            {LABELS[preset]}
          </option>
        ))}
      </Select>

      <Segmented
        className="hidden sm:inline-flex"
        label="Período"
        items={PRESETS}
        value={value.preset}
        onChange={(next) => {
          if (next === 'today' || next === '7d' || next === '30d') {
            onChange(rangeForPreset(next));
          }
        }}
      />

      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setDraft({ from: value.from, to: value.to });
          }
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Escolher período personalizado"
            className="inline-flex h-[38px] min-h-10 items-center gap-2 whitespace-nowrap rounded-xl border border-border bg-input px-3 text-13 font-medium text-text data-[state=open]:border-gold data-[state=open]:ring-[3px] data-[state=open]:ring-gold/15 sm:min-h-0"
          >
            <Icon name="doc" size={15} className="text-muted" />
            {value.preset === 'custom' ? label(value) : LABELS.custom}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-auto flex-col gap-3.5 bg-panel p-4">
          <CalendarMonth
            value={draft}
            months={2}
            onSelect={(next) => {
              setDraft(next);
            }}
          />
          <Divider />
          <div className="flex items-center justify-between gap-2">
            <span className="text-12 text-muted tabular-nums">
              {label({ preset: 'custom', ...draft })}
            </span>
            <div className="flex gap-2">
              <Btn
                kind="ghost"
                small
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancelar
              </Btn>
              <Btn
                kind="primary"
                small
                onClick={() => {
                  onChange({ preset: 'custom', ...draft });
                  setOpen(false);
                }}
              >
                Aplicar
              </Btn>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
